import { useRef } from "react";
import { motion, useScroll, useSpring } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { WA_LINK } from "../lib/data";
import { useLang } from "../lib/i18n";

const EASE = [0.16, 1, 0.3, 1];

export default function HowToOrder() {
  const { t } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start 75%", "end 55%"] });
  const lineScale = useSpring(scrollYProgress, { stiffness: 90, damping: 26 });

  return (
    <div className="mt-28" data-testid="how-to-order-section">
      <motion.div
        initial={{ opacity: 0, y: 28 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.8, ease: EASE }}
      >
        <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight">
          {t.stepsPre} <em className="font-serif italic font-medium text-brasslight">{t.stepsAccent}</em>
        </h2>
        <p className="mt-4 text-base md:text-lg text-taupe">{t.stepsSub}</p>
      </motion.div>

      <div ref={ref} className="relative mt-14 max-w-2xl">
        <div className="absolute start-[1.4rem] top-2 bottom-2 w-px bg-cream/10" aria-hidden="true" />
        <motion.div
          style={{ scaleY: lineScale }}
          className="absolute start-[1.4rem] top-2 bottom-2 w-px origin-top bg-gradient-to-b from-brass to-brasslight"
          aria-hidden="true"
        />
        <ol className="space-y-10">
          {t.steps.map((s, i) => (
            <motion.li
              key={i}
              initial={{ opacity: 0, x: -24 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.7, delay: i * 0.08, ease: EASE }}
              className="relative flex items-start gap-6"
              data-testid={`order-step-${i + 1}`}
            >
              <span className="relative z-10 flex h-[2.8rem] w-[2.8rem] shrink-0 items-center justify-center rounded-full border border-brass/40 bg-ink font-display text-sm font-extrabold text-brasslight shadow-[0_0_0_6px_rgba(28,26,23,1)]">
                {i + 1}
              </span>
              <div className="pt-1.5">
                <h3 className="font-display text-lg sm:text-xl font-bold tracking-tight">{s.title}</h3>
                <p className="mt-1 text-sm text-cream/60">{s.desc}</p>
              </div>
            </motion.li>
          ))}
        </ol>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-60px" }}
        transition={{ duration: 0.8, ease: EASE }}
        className="mt-14 text-center"
      >
        <a
          href={WA_LINK}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="start-your-order-button"
          className="pulse-ring inline-flex items-center gap-2.5 rounded-full bg-brass px-10 py-4 font-display text-base font-bold text-ink transition-all duration-300 hover:bg-brasslight active:scale-95"
        >
          {t.stepsCta} <ArrowUpRight size={18} strokeWidth={2.5} className="rtl:-scale-x-100" />
        </a>
      </motion.div>
    </div>
  );
}
